import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RealTimeEarningsWidget = () => {
  const [earnings, setEarnings] = useState({
    today: 2847.50,
    thisWeek: 18234.75,
    thisMonth: 76543.20,
    lastUpdate: new Date()
  });

  const [recentTransactions, setRecentTransactions] = useState([
    {
      id: 1,
      amount: 15.75,
      campaign: 'Facebook Auto Leads',
      buyer: 'Premium Insurance Co.',
      timestamp: new Date(Date.now() - 2 * 60 * 1000),
      status: 'completed'
    },
    {
      id: 2,
      amount: 14.25,
      campaign: 'Google Search Auto',
      buyer: 'SafeGuard Insurance',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      status: 'completed'
    },
    {
      id: 3,
      amount: 17.50,
      campaign: 'Direct Mail Home',
      buyer: 'HomeSecure Insurance',
      timestamp: new Date(Date.now() - 8 * 60 * 1000),
      status: 'pending'
    }
  ]);

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate real-time earnings updates
      const increment = Math.random() * 20;
      setEarnings(prev => ({
        ...prev,
        today: prev?.today + increment,
        thisWeek: prev?.thisWeek + increment,
        thisMonth: prev?.thisMonth + increment,
        lastUpdate: new Date()
      }));

      // Occasionally add new transactions
      if (Math.random() < 0.3) {
        const newTransaction = {
          id: Date.now(),
          amount: 10 + Math.random() * 15,
          campaign: ['Facebook Auto Leads', 'Google Search Auto', 'Direct Mail Home']?.[Math.floor(Math.random() * 3)],
          buyer: ['Premium Insurance Co.', 'SafeGuard Insurance', 'HomeSecure Insurance']?.[Math.floor(Math.random() * 3)],
          timestamp: new Date(),
          status: 'completed'
        };
        
        setRecentTransactions(prev => [newTransaction, ...prev?.slice(0, 4)]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'failed':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'XCircle';
      default:
        return 'HelpCircle';
    }
  };

  return (
    <div className="fixed top-20 right-4 z-1000">
      {/* Compact Widget */}
      <div className="bg-card border border-border rounded-lg shadow-card">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-4 text-left hover:bg-muted/30 transition-smooth rounded-lg"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full pulse-live"></div>
              <span className="text-sm font-medium text-foreground">Live Earnings</span>
            </div>
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={16} 
              className="text-muted-foreground" 
            />
          </div>
          <div className="text-2xl font-bold text-success">
            {formatCurrency(earnings?.today)}
          </div>
          <div className="text-xs text-muted-foreground">
            Today • Updated {formatTimestamp(earnings?.lastUpdate)}
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-border">
            {/* Earnings Summary */}
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">This Week</div>
                  <div className="text-lg font-bold text-foreground">
                    {formatCurrency(earnings?.thisWeek)}
                  </div>
                  <div className="flex items-center space-x-1 mt-1">
                    <Icon name="TrendingUp" size={12} className="text-success" />
                    <span className="text-xs text-success">+12.5%</span>
                  </div>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">This Month</div>
                  <div className="text-lg font-bold text-foreground">
                    {formatCurrency(earnings?.thisMonth)}
                  </div>
                  <div className="flex items-center space-x-1 mt-1">
                    <Icon name="TrendingUp" size={12} className="text-success" />
                    <span className="text-xs text-success">+8.3%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="border-t border-border">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-foreground">Recent Transactions</h4>
                  <button className="text-xs text-accent hover:text-accent/80 transition-smooth">
                    View All
                  </button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {recentTransactions?.map((transaction) => (
                    <div
                      key={transaction?.id}
                      className="flex items-center justify-between p-2 hover:bg-muted/30 rounded-lg transition-smooth"
                    >
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <Icon 
                          name={getStatusIcon(transaction?.status)} 
                          size={14} 
                          className={getStatusColor(transaction?.status)} 
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-success">
                            {formatCurrency(transaction?.amount)}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {transaction?.campaign}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatTimestamp(transaction?.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="border-t border-border p-3">
              <div className="grid grid-cols-2 gap-2">
                <button className="flex items-center justify-center space-x-1 p-2 bg-primary text-primary-foreground text-xs rounded hover:bg-primary/90 transition-smooth">
                  <Icon name="Download" size={12} />
                  <span>Export</span>
                </button>
                <button className="flex items-center justify-center space-x-1 p-2 bg-secondary text-secondary-foreground text-xs rounded hover:bg-secondary/90 transition-smooth">
                  <Icon name="BarChart3" size={12} />
                  <span>Analytics</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealTimeEarningsWidget;